/**
 * ServiceLikeButton — Botón de Me Gusta para SERVICIOS individuales.
 *
 * Mismo comportamiento que el LikeButton de creadoras pero apuntando a
 * /services/{id}/like. Acepta estado precargado (`initialLiked`,
 * `initialCount`) procedente del listado /models/{id}/services para evitar
 * un fetch extra al montar.
 *
 * - Toggle con actualización optimista.
 * - Sin sesión → toast "Inicia sesión para dar me gusta".
 * - Touch target ≥32×32 px en móvil (cumple WCAG 2.5.5 minimum).
 * - Badge "Muy popular" cuando count >= popularThreshold (5 por defecto).
 */
import { useState, useEffect, useCallback } from "react";
import { Heart, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "../lib/api";
const POPULAR_THRESHOLD_DEFAULT = 5;

export function ServiceLikeButton({
  serviceId,
  ownerId,
  initialCount = 0,
  initialLiked = false,
  popularThreshold = POPULAR_THRESHOLD_DEFAULT,
  className = "",
  showPopularBadge = true,
}) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(initialLiked);
  const [count, setCount] = useState(initialCount);
  const [loading, setLoading] = useState(false);

  // Sync if parent re-renders with new initial values (e.g. after services refetch)
  useEffect(() => {
    setLiked(initialLiked);
    setCount(initialCount);
  }, [serviceId, initialLiked, initialCount]);

  const isOwnService = user && ownerId && user.user_id === ownerId;

  const handleToggle = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!user) {
        toast.info("Inicia sesión para dar me gusta");
        return;
      }
      if (isOwnService) {
        toast.info("No puedes dar me gusta a tus propios servicios");
        return;
      }
      if (loading) return;

      // Optimistic update
      const nextLiked = !liked;
      const nextCount = nextLiked ? count + 1 : Math.max(0, count - 1);
      setLiked(nextLiked);
      setCount(nextCount);
      setLoading(true);

      try {
        const res = await axios.post(
          `${API}/services/${serviceId}/like`,
          {},
          { withCredentials: true }
        );
        setLiked(res.data.liked);
        setCount(res.data.count);
      } catch (err) {
        // Revert on error
        setLiked(liked);
        setCount(count);
        toast.error(err.response?.data?.detail || "Error al procesar el me gusta");
      } finally {
        setLoading(false);
      }
    },
    [serviceId, liked, count, loading, user, isOwnService]
  );

  const popular = count >= popularThreshold;

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={loading || isOwnService}
        data-testid={`service-like-btn-${serviceId}`}
        aria-label={liked ? "Quitar me gusta" : "Dar me gusta a este servicio"}
        aria-pressed={liked}
        className={`min-w-[36px] min-h-[36px] inline-flex items-center justify-center gap-1 px-2.5 rounded-full border-2 transition-all duration-200 font-heading font-semibold text-xs select-none
          ${liked
            ? "bg-neon-pink/15 border-neon-pink/60 text-neon-pink hover:bg-neon-pink/25"
            : "bg-zinc-900/70 border-zinc-700/60 text-zinc-400 hover:border-neon-pink/50 hover:text-neon-pink"
          }
          ${(loading || isOwnService) ? "opacity-60 cursor-not-allowed" : "cursor-pointer active:scale-95"}
        `}
      >
        <Heart
          className={`w-3.5 h-3.5 transition-all duration-200 ${liked ? "scale-110 drop-shadow-[0_0_4px_rgba(255,0,127,0.6)]" : ""}`}
          fill={liked ? "currentColor" : "none"}
          strokeWidth={2}
        />
        <span
          className="tabular-nums"
          data-testid={`service-like-count-${serviceId}`}
        >
          {count}
        </span>
      </button>
      {popular && showPopularBadge && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-[10px] font-heading font-bold uppercase tracking-wide text-amber-400"
          data-testid={`service-popular-badge-${serviceId}`}
          aria-label="Servicio muy popular"
        >
          <Sparkles className="w-2.5 h-2.5" /> Muy popular
        </span>
      )}
    </span>
  );
}
